import { Component, DestroyRef, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppBaseComponent } from '@shared/components/app-component-base';
import { ChartService } from '@shared/services/chart.service';
import { AppConstants } from '@core/constants';
import { ApiService } from '@core/services/api.service';
import { MetabolicService } from '@core/services/metabolic-service/metabolic.service';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { OptimizationOptionDialogComponent } from '@shared/dialogs/optimization-option-dialog/optimization-option-dialog.component';
import { catchError } from 'rxjs/operators';
import { String } from 'typescript-string-operations';
import { ConfirmDialogComponent, ConfirmDialogModel } from '@shared/dialogs/confirm-dialog/confirm-dialog.component';
import { PPD_ENTITY } from '@shared/enums/ppd-entity.enum';
import { MatDialogRef } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ITypicalErrorObject } from '@shared/components/interfaces/typical-error.interface';
import { HelperService } from '@shared/services/helper.service';
import { TestService } from '@core/services/test/test.service';
import { TestTypeStorageService } from '@shared/services/test-type-storage/test-type-storage.service';
import { WaffleFeatureFlagsService } from '@shared/services/feature-flags/waffle-feature-flags.service';
import { BehaviorSubject } from 'rxjs';
import { OnboardingService } from '@shared/services/onboarding/onboarding.service';
import { IAuth } from '@modules/login/login.component';
import { SagaStore } from '@features/onboarding/store/saga.store';

export interface IRecord {
  id: number | null;
  isSpecialAT?: boolean;
  type: string;
  hidden?: boolean;
  power: number;
  powerTolerance: number;
  speed?: number;
  work?: number;
  selected_power?: number[];
  duration: number;
  additionalValue: number;
  additionalValueUnit: string;
  additionalValueString?: string;
  additionalValueTolerance: number;
  initial_average_speed?: number;
  initial_average_power?: number;
  initial_duration_efforts?: number;
  dataForPOST: IDataForPost;
}

export interface IDataForPost {
  entity?: string;
  option?: number;
  target: number;
  range: number;
  upper: number;
  lower: number;
}

@Component({
  selector: 'app-ppd-test',
  templateUrl: './ppd-test.component.html',
  styleUrls: ['./ppd-test.component.scss'],
})
export class PpdTestComponent extends AppBaseComponent {
  private sagaStore = inject(SagaStore);

  public helperService: HelperService = inject(HelperService);
  public testService: TestService = inject(TestService);
  public featureFlagsService: WaffleFeatureFlagsService = inject(WaffleFeatureFlagsService);
  public onboardingService: OnboardingService = inject(OnboardingService);
  public onboardingProgress: BehaviorSubject<number> = this.onboardingService.getOnboardingPercentage();

  public showStar: boolean = false;
  public results: never[] = [];
  public progressValue: number = 0;
  public dataSource: any = [];
  public test: any = {};
  public isFinalCPCalculated: boolean = false;
  public itemData: any = {};
  public chartRendered: boolean = false;
  public athlete = [
    {
      header: 'ID',
    },
    {
      header: 'Athlete Name',
    },
    {
      header: 'Sport',
    },
    {
      header: 'Date',
    },
    {
      header: 'Tags',
    },
  ];

  public errors = {};
  public action: string = '';
  public testID: string = '';
  public data = {
    coaches: [],
    coach: null,
    countries: [],
    country: null,
    country_name: '',
  };
  public modules = [
    {
      id: 'test_data',
      type: 7,
      rpId: 'test_data_rp',
      text: 'Test Data',
      name: 'td',
      state: 'active',
      isShow: true,
    },
  ];
  public chartData: any = [];
  public isAcceptAndView: boolean = false;
  public isCallingAction: boolean = false;
  public rawSelectedValues: any;
  public isShowTypicalError: boolean = false;
  public typicalErrorList: ITypicalErrorObject[] = [];

  constructor(
    private route: ActivatedRoute,
    private metabolicService: MetabolicService,
    private apiService: ApiService,
    private chartService: ChartService,
    private router: Router,
    private snackBar: MatSnackBar,
    private readonly dialog: MatDialog,
    private destroyRef: DestroyRef,
    private testTypeStorageService: TestTypeStorageService
  ) {
    super();
  }

  public onInitPage(): void {
    this.testID = this.route.snapshot.params['id'];
    this.action = this.route.snapshot.params['action'];
    this.loadTests();
  }

  public onLoadPage(): void {
    this.chartRendered = false;
    this.loadTests();
  }

  public loadTests(): void {
    if (this.action) {
      this.metabolicService
        .fetchTestDataDelete({ testId: this.testID })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response: any): void => {
          this.callbackTests(response);
        });
    } else {
      this.metabolicService
        .fetchTestData({ testId: this.testID })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response: any): void => {
          this.callbackTests(response);
        });
    }
  }

  public callbackTests(response: any): void {
    if (response.results.length) {
      this.dataSource = response.results;
      this.test = {
        id: this.dataSource[0].id,
        date: this.dataSource[0].test_date,
        location: this.dataSource[0].location,
        athlete: this.dataSource[0].athlete.first_name + ' ' + this.dataSource[0].athlete.last_name,
        coach: this.auth.first_name + ' ' + this.auth.last_name,
        email: this.auth.email,
        test_type: this.dataSource[0].test_type,
        sport: this.dataSource[0].sport,
        test_id: this.dataSource[0].test_id,
      };

      this.test.origin = this.dataSource[0];

      this.loadCharts();
    } else {
      this.snackBar.open('Tests not found', 'OK', AppConstants.TOAST_CONFIG.ERROR);
    }
  }

  public loadCharts(): void {
    if (!this.chartRendered) this.fetchDataAllTab(this.modules);
  }
  fetchDataAllTab(listElements: any) {
    this.metabolicService
      .fetchChartData(false, this.testID)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: any): void => {
        // Render charts
        if (response && response.length > 0) {
          // Optimization only take the last chart data
          this.chartData = [response[listElements[0].type - 1]];
          this.chartRendered = true;

          this.itemData = {
            data: this.chartData[0],
            testID: this.testID,
            test: this.test,
          };
        }
      });
  }

  public setUniqueEntity(entityArray: string[], entity: string): void {
    if (entityArray.indexOf(entity) == -1) {
      entityArray.push(entity);
    }
  }

  public updateProgressBaseOnNumberOfEntities(measuredValues: any): void {
    this.rawSelectedValues = measuredValues.rawSelectedValues;

    let isSpecialAT: IRecord[] = measuredValues.dataSource.filter((x: IRecord) => x.isSpecialAT);
    let filteredValues: IRecord[] = measuredValues.dataSource.filter((x: IRecord) => !x.isSpecialAT);

    let uniqueEntities: any[] = Array.from(new Set(filteredValues.map((x: any) => x.dataForPOST.entity)));

    let numberOfEntities: number = filteredValues.length;

    if (isSpecialAT.length > 2) {
      this.setUniqueEntity(uniqueEntities, PPD_ENTITY.AT);
      numberOfEntities++;
    }

    this.showStar = numberOfEntities > uniqueEntities.length;
    this.progressValue = this.calculateBaseProgress(uniqueEntities.length);

    if (isSpecialAT.length === 2) {
      this.progressValue = this.addSpecialATProgress(this.progressValue);
    }

    this.progressValue = Math.min(this.progressValue, 100);
    if (this.progressValue < 100) {
      // Clear traffic lights if the progress bar is not 100%
      this.typicalErrorList = [];
      this.isShowTypicalError = false;
    }

    if (this.progressValue < 100) {
      this.isFinalCPCalculated = false;
    }
  }

  private calculateBaseProgress(uniqueEntitiesLength: number): number {
    if (uniqueEntitiesLength > 2) {
      return 100;
    } else if (uniqueEntitiesLength > 1) {
      return 67;
    } else if (uniqueEntitiesLength > 0) {
      return 33;
    } else {
      return 0;
    }
  }

  private addSpecialATProgress(currentProgress: number): number {
    if (currentProgress === 0) {
      return 33;
    } else if (currentProgress === 33) {
      return 67;
    } else if (currentProgress === 67) {
      return 100;
    } else {
      return Math.min(currentProgress + 33, 100);
    }
  }

  public accept(isAcceptAndView: boolean): void {
    this.openOptimizationOptionsModal(isAcceptAndView);
  }

  public handleCancelView(isManual: boolean, isPPD: boolean, isVirtual: boolean, isLactate: boolean): void {
    this.testTypeStorageService.resetConfig();
    this.testTypeStorageService.setTestConfig({
      isManual,
      isPPD,
      isVirtual,
      isLactate,
      testId: this.testID,
      isBack: true,
    });
    this.router.navigate(['/home/ppd-test']);
  }

  public openOptimizationOptionsModal(isAcceptAndView: boolean): void {
    const type: string = AppConstants.TEST_TYPES.PPD;
    let modalUpdate = this.dialog.open(OptimizationOptionDialogComponent, {
      panelClass: 'general-dialog',
      disableClose: true,
      data: {
        type,
      },
    });

    modalUpdate
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any): void => {
        if (!data || data.action !== 'confirm') {
          return;
        }
        this.doTestOptimization(isAcceptAndView, data);
      });
  }

  public doTestOptimization(isAcceptView: boolean, data: any): void {
    let param_array: any[] = [];
    let params: string = '';

    if (data && data.action == 'confirm') {
      if (data.productId) {
        param_array.push('product=' + data.productId);
      }
      if (data.planId) {
        param_array.push('plan=' + data.planId);
      }
      params = param_array.join('&');
    }
    const api: string = `tests/${this.testID}/accept/${params ? '?' + params : ''}`;
    this.apiService
      .put(api, {})
      .pipe(
        catchError((error) => {
          const paramErrors = error.split(' ').slice(1); // Ignore the initial 'Error: ' prefix
          if (error === 'Error: TEST_ALREADY_ACTIVE') {
            this.router.navigateByUrl('home/metabolic-profile/' + this.testID + '/');
          }
          if (paramErrors.length > 0 && paramErrors.indexOf('500') > -1) {
            let testName: string = '';
            if (paramErrors[1] == 'newtype') testName = 'PPD test';
            else if (paramErrors[1] == AppConstants.TEST_TYPES.LACTATE) testName = 'Lactate test';
            else if (paramErrors[1] == AppConstants.TEST_TYPES.VIRTUAL) testName = 'Virtual test';
            else if (paramErrors[1] == AppConstants.TEST_TYPES.MANUAL) testName = 'Manual test';
            else if (paramErrors[1] == AppConstants.TEST_TYPES.CRITICAL_POWER) testName = 'critical power test';
            this.isCallingAction = false;
            this.popupPurchase(testName, error);
          } else {
            this.snackBar.open(error, 'OK', this.constant.TOAST_CONFIG.ERROR);
          }
          throw error;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((): void => {
        this.handleRedirect(this.testID, isAcceptView);
      });
  }
  public handleRedirect(testId: string, isAcceptView: boolean): void {
    this.sagaStore.recordEvent('ppdCreated');
    if (!isAcceptView) {
      this.router.navigateByUrl('home/manage/tests');
    } else {
      this.router.navigateByUrl('home/apc/setcard/' + testId);
    }
  }

  private popupPurchase(testName: string, error: string): void {
    const paramErrors: string[] = error.split(' ');
    const message = this.testService.excessTestConfirmationText(testName, error);
    const dialogData: ConfirmDialogModel = new ConfirmDialogModel('Confirmation', message);
    const dialogRef: MatDialogRef<ConfirmDialogComponent> = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: dialogData,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result): void => {
        if (result) {
          const params1: string = '?plan=' + paramErrors[3] + '&type=' + paramErrors[2] + '&new_id=' + paramErrors[4];
          const apiUrl: string = String.Format(this.constant.API.INVOICES.PURCHASE_EXCEED, params1);

          this.apiService
            .post(apiUrl, {})
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(
              (): void => {
                this.handleRedirect(this.testID, this.isAcceptAndView);
              },
              (errorPurchase): void => {
                this.snackBar.open(errorPurchase, 'OK', this.constant.TOAST_CONFIG.ERROR);
              }
            );
        }
      });
  }

  public performCPTest(): void {
    const apiUrl: string = 'tests/' + this.testID + '/optimize_solution/';
    this.customRendererService.empty('#chartPPD');
    this.isFinalCPCalculated = false;
    this.isShowTypicalError = false;

    this.apiService
      .post(apiUrl, { raw_selected_values: this.rawSelectedValues })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res: any): void => {
          if (res) {
            if (res.warning) {
              this.snackBar.open(res.warning, 'OK', AppConstants.TOAST_CONFIG.WARNING);
            } else {
              this.checkTrafficLight(res);
              this.isFinalCPCalculated = true;
              this.chartService.generatePPDChart('#chartPPD', res, false);
              this.customRendererService.hide(AppConstants.MAT_SPINNER_CLASS);
            }
          } else {
            this.snackBar.open('Cannot find any solution for measured values.', 'OK', AppConstants.TOAST_CONFIG.ERROR);
          }
        },
        (): void => {
          this.snackBar.open('Cannot find any solution for measured values.', 'OK', AppConstants.TOAST_CONFIG.ERROR);
        }
      );
  }

  public checkTrafficLight(res: any): void {
    try {
      const trafficLightArray: any = res.data_accuracy.traffic_lights;
      if (this.helperService.isArray(trafficLightArray)) {
        this.typicalErrorList = trafficLightArray;
        this.isShowTypicalError = true;
      } else {
        this.snackBar.open('The traffic lights has incorrect format', 'OK', AppConstants.TOAST_CONFIG.WARNING);
      }
    } catch (e: any) {
      this.snackBar.open('The error indication has invalid data', 'OK', AppConstants.TOAST_CONFIG.ERROR);
    }
  }
}
